import type { CommerceSyncHealth, SellerConnection } from '../../api/amazonconnect';
import { fetchCommerceHealth } from '../commerceHealth';
import { dayAggMap, fetchOrdersMetricsSeries } from '../ordersTruth';
import { returnsByDay } from '../reportFacts';
import { fetchReportPage } from '../reportPages';
import type { CompareMode, DatePreset } from '../types';
import { compareWindow, periodWindow } from '../executive-kpis/periodWindows';
import { buildOverlayModel } from './buildSeries';
import type { MasterOverlayModel } from './types';

function staleHours(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / 3_600_000;
}

export async function loadMasterOverlay(opts: {
  connection: SellerConnection | null;
  health: CommerceSyncHealth | null;
  datePreset: DatePreset;
  compare: CompareMode;
}): Promise<MasterOverlayModel> {
  const current = periodWindow(opts.datePreset);
  const prior = compareWindow(current, opts.compare);
  const conn = opts.connection;
  const connectionId = conn?.id ?? null;

  if (!connectionId) {
    return buildOverlayModel({
      connectionId: null,
      connectionStatus: 'unknown',
      current,
      prior,
      orders: [],
      truncated: false,
      staleHours: null,
      error: null,
    });
  }

  const ordersRow = opts.health?.domains?.find(
    (d) => d.marketsync_domain === 'orders' || d.key === 'orders',
  );
  const lastSync = ordersRow?.last_sync_at || conn.last_sync_at || null;

  try {
    const [currentSeries, priorSeries, returnsPage] = await Promise.all([
      fetchOrdersMetricsSeries(connectionId, {
        from: current.isoFrom,
        to: current.isoTo,
        grain: 'day',
      }),
      prior
        ? fetchOrdersMetricsSeries(connectionId, {
            from: prior.isoFrom,
            to: prior.isoTo,
            grain: 'day',
          })
        : Promise.resolve(null),
      fetchReportPage(connectionId, 'returns'),
    ]);
    const dayAggs = dayAggMap(
      [...(currentSeries.buckets ?? []), ...(priorSeries?.buckets ?? [])],
      'Asia/Kolkata',
      currentSeries.currency,
    );
    return buildOverlayModel({
      connectionId,
      connectionStatus: conn.status,
      current,
      prior,
      orders: [],
      dayAggs,
      truncated: false,
      staleHours: staleHours(lastSync),
      error: null,
      source: currentSeries.source,
      currency: currentSeries.currency,
      extraDay: { returns: returnsByDay(returnsPage) },
    });
  } catch (e) {
    return buildOverlayModel({
      connectionId,
      connectionStatus: conn.status,
      current,
      prior,
      orders: [],
      truncated: false,
      staleHours: staleHours(lastSync),
      error: e instanceof Error ? e.message : 'Failed to load overlay series',
    });
  }
}

export async function loadMasterOverlayWithHealth(opts: {
  connection: SellerConnection | null;
  datePreset: DatePreset;
  compare: CompareMode;
}): Promise<MasterOverlayModel> {
  let health: CommerceSyncHealth | null = null;
  if (opts.connection?.id) {
    try {
      health = await fetchCommerceHealth(opts.connection.id);
    } catch {
      health = null;
    }
  }
  return loadMasterOverlay({ ...opts, health });
}
